// Application Insights — SDK JavaScript officiel (CCT-193), bundle WEB uniquement.
//
// Ce fichier est choisi par webpack (`resolve.extensions` place `.web.ts` avant `.ts`)
// pour react-native-web ; sur mobile natif, Metro résout `appInsights.ts` (stub no-op)
// et le SDK n'entre JAMAIS dans le bundle natif.
//
// COMPLÉMENT (et non remplacement) du beacon maison `./beacon` :
//   - beacon    -> `POST /insights/events`, ré-émis en spans OTel par le backend
//                  (SEUL chemin disponible sur mobile natif, inchangé) ;
//   - ce module -> pages vues, sessions, utilisateurs, navigateur, géo, appels
//                  réseau (fetch/XHR) et exceptions, NATIVEMENT.
//
// PROVENANCE DE LA CONNECTION STRING (jamais en dur dans le source) :
//   ressource `azurerm_application_insights` provisionnée par Terraform
//   (variable `enable_monitoring`) ; valeur = output Terraform
//   `application_insights_connection_string` ou portail Azure > Application
//   Insights > Overview > « Connection String ». Elle est injectée AU BUILD par
//   `.github/workflows/cc-jaw-web-deploy.yml` via `scripts/set-web-insights.mjs`,
//   qui écrit `app/config/insights.generated.ts` (gitignoré).
//
// ABSENTE => ce module NE FAIT RIEN (aucune exception, aucun réseau).

import { ApplicationInsights, DistributedTracingModes } from '@microsoft/applicationinsights-web';
import type { ITelemetryItem } from '@microsoft/applicationinsights-web';

import {
  APPINSIGHTS_CONNECTION_STRING,
  APPINSIGHTS_CORRELATION_DOMAINS,
  APPINSIGHTS_ENABLED,
} from '../config/insights.generated';
import { currentEnv, resolveApiBase } from '../config/env';
import { getSessionId } from './session';

let _ai: ApplicationInsights | null = null;
let _started = false;

/**
 * Domaines autorisés à recevoir les en-têtes de corrélation (`traceparent` W3C).
 * Sans cette liste, le SDK n'instrumente que le même origine et la trace s'arrête
 * au navigateur : les appels vers l'API (autre domaine) ne rejoindraient pas les
 * spans backend.
 */
function correlationDomains(): string[] {
  const out: string[] = [];
  const push = (d: string | undefined) => {
    const v = (d || '').trim().toLowerCase();
    if (v && !out.includes(v)) out.push(v);
  };
  try {
    push(new URL(resolveApiBase()).hostname); // backend réellement appelé par CE build
  } catch {
    /* base relative/vide : ignoré */
  }
  push('dev.ws.cloudchrono.com');
  push('ws.cloudchrono.com');
  push('*.cloudchrono.com');
  push('*.azurecontainerapps.io');
  for (const d of APPINSIGHTS_CORRELATION_DOMAINS) push(d);
  return out;
}

/**
 * Initialise le SDK. Idempotent, best-effort, no-op sans connection string.
 * À appeler UNE fois au bootstrap, AVANT tout rendu (donc avant l'auth) : la
 * session anonyme est tracée, puis rattachée à l'utilisateur par
 * `setInsightsUser()` dès qu'une session authentifiée est appliquée.
 */
export function initAppInsights(): void {
  if (_started) return;
  if (typeof window === 'undefined') return;
  const cs = (APPINSIGHTS_CONNECTION_STRING || '').trim();
  if (!cs || !APPINSIGHTS_ENABLED) return;
  _started = true;

  try {
    const env = currentEnv();
    const ai = new ApplicationInsights({
      config: {
        connectionString: cs,

        // --- Collecte native ---
        enableAutoRouteTracking: true, // react-navigation (web) pousse l'history -> pageViews
        autoTrackPageVisitTime: true,
        disableFetchTracking: false,
        disableAjaxTracking: false,
        disableExceptionTracking: false,
        enableUnhandledPromiseRejectionTracking: true,

        // --- Corrélation distribuée W3C vers les backends OTel ---
        distributedTracingMode: DistributedTracingModes.W3C,
        enableCorsCorrelation: true,
        correlationHeaderDomains: correlationDomains(),

        disableCookiesUsage: false, // requis pour sessions/utilisateurs natifs
        loggingLevelConsole: 0,
      },
    });

    ai.loadAppInsights();

    // Propriété commune à TOUS les événements : l'id de session maison, qui est
    // aussi envoyé au backend (header X-CC-Session) et porté par les spans OTel
    // sous l'attribut `cc.session_id` -> jointure KQL front/back.
    //
    // ⚠️ NOM DE LA CLÉ — CCT-204. `envelope.data.X` devient `customDimensions["X"]`. Les
    // spans backend posent `cc.session_id` et TOUTES les requêtes KQL de l'écran BO
    // « Sessions » joignent sur CE nom. Ce module posait `ccSessionId` : les pages vues et
    // événements du navigateur étaient bien ingérés, mais rattachés à AUCUNE session.
    // `ccSessionId` reste en doublon pour les données déjà ingérées (la lecture fait un
    // `coalesce` des deux le temps de la rétention).
    ai.addTelemetryInitializer((envelope: ITelemetryItem) => {
      try {
        const sid = getSessionId();
        envelope.data = envelope.data || {};
        envelope.data['cc.session_id'] = sid;   // nom canonique, aligné sur les spans backend
        envelope.data.ccSessionId = sid;        // legacy : lu par les données déjà ingérées
        envelope.data.ccEnv = env;
        envelope.data.ccApp = 'cc-jaw-app';
        envelope.data['cc.host'] = window.location.hostname;
      } catch {
        /* best-effort */
      }
      return true;
    });

    ai.trackPageView();
    trackClicks(ai);   // CCT-217 : les ACTIONS, que le SDK ne collecte pas nativement
    _ai = ai;
  } catch {
    // Best-effort : une télémétrie cassée ne doit JAMAIS casser l'app.
    _ai = null;
  }
}

/**
 * Collecte des ACTIONS utilisateur (CCT-217).
 *
 * POURQUOI. Le SDK n'émet nativement que des PAGES VUES (`enableAutoRouteTracking`) : il ne
 * trace aucun clic. D'où le constat qui a motivé ce ticket — filtrer une session de 150
 * événements sur la source « client » n'en montrait que 2. Ce n'était pas une perte de
 * données : il n'y avait réellement que 2 événements client à afficher.
 *
 * COMMENT. Un écouteur unique sur `document`, en phase de CAPTURE : il voit le clic même si
 * un handler applicatif l'arrête ensuite (`stopPropagation`), et il ne dépend d'aucun
 * composant. Passif et best-effort — il n'appelle jamais `preventDefault`, et toute erreur
 * est avalée : la télémétrie ne casse pas une interface.
 *
 * VOLUME. Un clic = un événement, très en dessous du polling déjà filtré côté serveur. Deux
 * garde-fous quand même : on ne remonte que les éléments réellement ACTIONNABLES (bouton,
 * lien, champ, `role=button`…) — pas chaque clic sur du texte — et on déduplique les clics
 * identiques rapprochés (double-clic, bouton qui se re-rend).
 */
const CLICK_DEDUP_MS = 400;
let _lastClick = { key: "", at: 0 };

function actionableTarget(start: EventTarget | null): HTMLElement | null {
  let el = start instanceof HTMLElement ? start : null;
  for (let depth = 0; el && depth < 6; depth += 1, el = el.parentElement) {
    if (el.dataset?.ccAction) return el;
    const tag = el.tagName;
    if (tag === "BUTTON" || tag === "A" || tag === "SUMMARY" || tag === "OPTION") return el;
    if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA" || tag === "LABEL") return el;
    const role = el.getAttribute("role");
    if (role === "button" || role === "tab" || role === "menuitem" || role === "link") return el;
  }
  return null;
}

/** Libellé lisible d'un élément, par ordre d'intention décroissante. */
function labelOf(el: HTMLElement): string {
  const explicit = el.dataset?.ccAction
    || el.getAttribute("aria-label")
    || el.getAttribute("title")
    || (el as HTMLInputElement).name
    || el.id;
  const text = (explicit || el.textContent || "").replace(/\s+/g, " ").trim();
  return text.slice(0, 80);
}

export function trackClicks(ai: ApplicationInsights): void {
  if (typeof document === "undefined") return;
  try {
    document.addEventListener("click", (e) => {
      try {
        const el = actionableTarget(e.target);
        if (!el) return;
        const label = labelOf(el);
        // Un élément actionnable sans le moindre libellé n'apprend rien : on le laisse.
        if (!label) return;
        const key = `${el.tagName}:${label}`;
        const now = Date.now();
        if (key === _lastClick.key && now - _lastClick.at < CLICK_DEDUP_MS) return;
        _lastClick = { key, at: now };
        ai.trackEvent({ name: "click" }, {
          "cc.event.action": label,
          "cc.event.type": el.tagName.toLowerCase(),
          "cc.event.path": window.location.pathname,
          "cc.event.source": "client",
        });
      } catch {
        /* best-effort : une erreur d'instrumentation ne remonte jamais à l'UI */
      }
    }, { capture: true, passive: true });
  } catch {
    /* navigateur sans addEventListener exploitable : on s'abstient */
  }
}

// L'identifiant utilisateur App Insights interdit les espaces et `,;=|`.
function sanitizeUserId(v: string): string {
  return v.trim().replace(/[,;=| ]+/g, '_');
}

/** Rattache la session anonyme à l'utilisateur authentifié. No-op si SDK inactif. */
export function setInsightsUser(email?: string | null, accountId?: string | number | null): void {
  try {
    if (!_ai || !email) return;
    const id = sanitizeUserId(String(email));
    if (!id) return;
    const acct =
      accountId === null || accountId === undefined ? undefined : sanitizeUserId(String(accountId));
    _ai.setAuthenticatedUserContext(id, acct, true);
  } catch {
    /* best-effort */
  }
}

/** Oublie l'utilisateur authentifié (déconnexion) ; la session anonyme continue. */
export function clearInsightsUser(): void {
  try {
    _ai?.clearAuthenticatedUserContext();
  } catch {
    /* best-effort */
  }
}
